import React from "react";
import { toast, type Id, type ToastPosition } from "react-toastify";
import "./Toast.scss";
import { SeemaOK } from "../../components";
import { IconButton } from "../../controls";

export interface ToastProps {
  message?: React.ReactNode;
  description?: React.ReactNode;
  "data-testid"?: string;
}

export interface ShowToastProps {
  message?: React.ReactElement | string;
  description?: React.ReactElement | string;
  container?: string;
  style?: string;
  position?: ToastPosition;
  hideProgressBar?: boolean;
  autoClose?: string | number | false;
  component?: React.ReactElement;
  className?: string;
  "data-testid"?: string;
  closeOnClick?: boolean;
  toastId?: Id | null;
  draggable?: boolean;
  closeButton?: ({
    closeToast,
  }: {
    closeToast: () => void;
  }) => React.ReactElement;
}

export interface UpdateToastProps {
  toastId: Id;
  content: React.ReactNode;
}

const Toast: React.FC<ToastProps> = ({
  message,
  description,
  "data-testid": dataTestId,
}) => {
  return (
    <div className={`toast-box-wrapper`}>
      <SeemaOK />
      <div className="toast-message-wrap">
        <div className={`toast-message`} data-testid={`${dataTestId}-message`}>
          {message}
        </div>
        {description && (
          <div className={`toast-description`}>{description}</div>
        )}
      </div>
    </div>
  );
};

export const showToast = ({
  message = undefined,
  description = undefined,
  container = undefined,
  style = "popup",
  position = undefined,
  hideProgressBar = false,
  autoClose = "5000",
  component = undefined,
  className = "",
  "data-testid": dataTestId = "",
  closeOnClick = true,
  toastId = null,
  draggable = false,
  closeButton = ({ closeToast }) => (
    <IconButton
      data-testid="toast-close-button"
      isIcon="seem-icon-x-small"
      size="s"
      onClick={closeToast}
      className="custom-toast-close-button"
    />
  ),
}: ShowToastProps): Id => {
  return toast(
    component || (
      <Toast
        message={message}
        description={description}
        data-testid={`${dataTestId}-toast`}
      />
    ),
    {
      containerId: container || "toast-root",
      position: position || "top-center",
      hideProgressBar: hideProgressBar || false,
      // `autoClose` defaults to the string "5000"; react-toastify tolerates a
      // string at runtime but types it as `number | false`. Cast the exact value
      // through without changing it to preserve runtime behavior.
      autoClose: autoClose as unknown as number | false,
      className: className,
      closeOnClick: closeOnClick,
      // `toastId` defaults to `null` (our "no id" sentinel); react-toastify
      // types this slot as `Id | undefined`. Assert without changing the runtime
      // value — toastify treats a falsy toastId as "auto-generate".
      toastId: toastId as Id | undefined,
      closeButton,
    }
  );
};

export const updateToast = ({ toastId, content }: UpdateToastProps): void => {
  toast.update(toastId, { render: content });
};

export const dismissToast = (id?: Id): void => {
  toast.dismiss(id);
};

export const dismissAllToasts = (): void => {
  toast.dismiss();
};

export default Toast;
